import React from 'react';
import { 
  Facebook, 
  Instagram, 
  Twitter, 
  Linkedin, 
  Mail, 
  Phone, 
  MapPin, 
  Globe, 
  Plus, 
  Trash2 
} from 'lucide-react';

interface FooterEditorProps {
  settings: any;
  content: any;
  onSettingsChange: (settings: any) => void;
  onContentChange: (content: any) => void;
}

const FooterEditor: React.FC<FooterEditorProps> = ({ 
  settings, 
  content, 
  onSettingsChange, 
  onContentChange 
}) => {
  const handleSettingChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const newValue = type === 'checkbox' 
      ? (e.target as HTMLInputElement).checked 
      : type === 'number' 
        ? parseInt(value) 
        : value;
    
    onSettingsChange({ [name]: newValue });
  };
  
  const handleColumnChange = (index: number, field: string, value: string) => {
    const newColumns = [...(content?.columns || [])];
    newColumns[index] = { ...newColumns[index], [field]: value };
    onContentChange({ columns: newColumns });
  };
  
  const handleAddColumn = () => {
    const newColumns = [...(content?.columns || [])];
    newColumns.push({ title: '', links: [] });
    onContentChange({ columns: newColumns });
  };
  
  const handleRemoveColumn = (index: number) => {
    const newColumns = [...(content?.columns || [])];
    newColumns.splice(index, 1);
    onContentChange({ columns: newColumns });
  };
  
  const handleAddLink = (columnIndex: number) => {
    const newColumns = [...(content?.columns || [])];
    if (!newColumns[columnIndex].links) {
      newColumns[columnIndex].links = [];
    }
    newColumns[columnIndex].links.push({ label: '', url: '' });
    onContentChange({ columns: newColumns });
  };
  
  const handleLinkChange = (columnIndex: number, linkIndex: number, field: string, value: string) => {
    const newColumns = [...(content?.columns || [])];
    newColumns[columnIndex].links[linkIndex] = { 
      ...newColumns[columnIndex].links[linkIndex], 
      [field]: value 
    };
    onContentChange({ columns: newColumns });
  };
  
  const handleRemoveLink = (columnIndex: number, linkIndex: number) => {
    const newColumns = [...(content?.columns || [])];
    newColumns[columnIndex].links.splice(linkIndex, 1);
    onContentChange({ columns: newColumns });
  };
  
  const handleSocialChange = (network: string, value: string) => {
    const newSocial = { ...(content?.social || {}) };
    newSocial[network] = value;
    onContentChange({ social: newSocial });
  };
  
  return (
    <div className="space-y-6">
      {/* General Settings */}
      <div className="space-y-4">
        <h3 className="text-sm font-medium text-slate-700">Genel Ayarlar</h3>
        
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="block text-sm text-slate-600">
              Logo Metni
            </label>
            <input
              type="text"
              name="logoText"
              value={settings?.logoText || 'KREDİPARK'}
              onChange={handleSettingChange}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
            />
          </div>
          
          <div className="space-y-2">
            <label className="block text-sm text-slate-600">
              Slogan
            </label>
            <input
              type="text"
              name="slogan"
              value={settings?.slogan || 'Türkiye\'nin en kapsamlı kredi ve banka karşılaştırma platformu'}
              onChange={handleSettingChange}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
            />
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="block text-sm text-slate-600">
              Arka Plan Rengi
            </label>
            <div className="flex gap-2">
              <input
                type="color"
                name="backgroundColor"
                value={settings?.backgroundColor || '#ffffff'}
                onChange={handleSettingChange}
                className="w-10 h-10 p-1 border border-slate-200 rounded-lg"
              />
              <input
                type="text"
                name="backgroundColor"
                value={settings?.backgroundColor || '#ffffff'}
                onChange={handleSettingChange}
                className="flex-1 px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <label className="block text-sm text-slate-600">
              Metin Rengi
            </label>
            <div className="flex gap-2">
              <input
                type="color"
                name="textColor"
                value={settings?.textColor || '#1e293b'}
                onChange={handleSettingChange}
                className="w-10 h-10 p-1 border border-slate-200 rounded-lg"
              />
              <input
                type="text"
                name="textColor"
                value={settings?.textColor || '#1e293b'}
                onChange={handleSettingChange}
                className="flex-1 px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
              />
            </div>
          </div>
        </div>
      </div>
      
      {/* Contact Information */}
      <div className="space-y-4">
        <h3 className="text-sm font-medium text-slate-700">İletişim Bilgileri</h3>
        
        <div className="space-y-2">
          <label className="block text-sm text-slate-600">
            Adres
          </label>
          <div className="relative">
            <MapPin className="absolute left-3 top-3 text-slate-400" size={16} />
            <textarea
              name="address"
              value={content?.address || 'Kuştepe Mah. Mecidiyeköy Yolu Cad. Trump Tower No:12 İç Kapı No: 221 Şişli İstanbul'}
              onChange={(e) => onContentChange({ address: e.target.value })}
              rows={2}
              className="w-full pl-10 pr-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
            />
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="block text-sm text-slate-600">
              Telefon
            </label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={16} />
              <input
                type="text"
                name="phone"
                value={content?.phone || '0(212) 909 1133'}
                onChange={(e) => onContentChange({ phone: e.target.value })}
                className="w-full pl-10 pr-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <label className="block text-sm text-slate-600">
              E-posta
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={16} />
              <input
                type="email"
                name="email"
                value={content?.email || 'iletisim@kredipark.com'}
                onChange={(e) => onContentChange({ email: e.target.value })}
                className="w-full pl-10 pr-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
              />
            </div>
          </div>
        </div>
        
        <div className="space-y-2">
          <label className="block text-sm text-slate-600">
            Website
          </label>
          <div className="relative">
            <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={16} />
            <input
              type="url"
              name="website"
              value={content?.website || 'https://www.kredipark.com'}
              onChange={(e) => onContentChange({ website: e.target.value })}
              className="w-full pl-10 pr-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
            />
          </div>
        </div>
      </div>
      
      {/* Social Media */}
      <div className="space-y-4">
        <h3 className="text-sm font-medium text-slate-700">Sosyal Medya</h3>
        
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="block text-sm text-slate-600">
              Facebook
            </label>
            <div className="relative">
              <Facebook className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={16} />
              <input
                type="url"
                value={content?.social?.facebook || ''}
                onChange={(e) => handleSocialChange('facebook', e.target.value)}
                placeholder="https://www.facebook.com/kredipark"
                className="w-full pl-10 pr-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <label className="block text-sm text-slate-600">
              Instagram
            </label>
            <div className="relative">
              <Instagram className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={16} />
              <input
                type="url"
                value={content?.social?.instagram || ''}
                onChange={(e) => handleSocialChange('instagram', e.target.value)}
                placeholder="https://www.instagram.com/kredipark"
                className="w-full pl-10 pr-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
              />
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="block text-sm text-slate-600">
              Twitter
            </label>
            <div className="relative">
              <Twitter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={16} />
              <input
                type="url"
                value={content?.social?.twitter || ''}
                onChange={(e) => handleSocialChange('twitter', e.target.value)}
                placeholder="https://twitter.com/kredipark"
                className="w-full pl-10 pr-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <label className="block text-sm text-slate-600">
              LinkedIn
            </label>
            <div className="relative">
              <Linkedin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={16} />
              <input
                type="url"
                value={content?.social?.linkedin || ''}
                onChange={(e) => handleSocialChange('linkedin', e.target.value)}
                placeholder="https://www.linkedin.com/company/kredipark"
                className="w-full pl-10 pr-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
              />
            </div>
          </div>
        </div>
      </div>
      
      {/* Footer Columns */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium text-slate-700">Footer Sütunları</h3>
          <button
            onClick={handleAddColumn}
            className="px-2 py-1 text-xs bg-slate-100 text-slate-700 rounded hover:bg-slate-200 transition-colors"
          >
            Sütun Ekle
          </button>
        </div>
        
        <div className="space-y-4">
          {(content?.columns || []).map((column: any, index: number) => (
            <div key={index} className="border border-slate-200 rounded-lg p-4 space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-medium text-slate-700">Sütun {index + 1}</h4>
                <button
                  onClick={() => handleRemoveColumn(index)}
                  className="p-1 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <Trash2 size={16} />
                </button>
              </div>
              
              <div className="space-y-2">
                <label className="block text-xs text-slate-500">
                  Başlık
                </label>
                <input
                  type="text"
                  value={column.title || ''}
                  onChange={(e) => handleColumnChange(index, 'title', e.target.value)}
                  placeholder="Sütun Başlığı"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
                />
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="block text-xs text-slate-500">
                    Linkler
                  </label>
                  <button
                    onClick={() => handleAddLink(index)}
                    className="px-2 py-1 text-xs bg-slate-100 text-slate-700 rounded hover:bg-slate-200 transition-colors"
                  >
                    Link Ekle
                  </button>
                </div>
                
                <div className="space-y-2">
                  {(column.links || []).map((link: any, linkIndex: number) => (
                    <div key={linkIndex} className="flex items-center gap-2">
                      <div className="grid grid-cols-2 gap-2 flex-1">
                        <input
                          type="text"
                          value={link.label || ''}
                          onChange={(e) => handleLinkChange(index, linkIndex, 'label', e.target.value)}
                          placeholder="Link Etiketi"
                          className="px-2 py-1 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
                        />
                        <input
                          type="text"
                          value={link.url || ''}
                          onChange={(e) => handleLinkChange(index, linkIndex, 'url', e.target.value)}
                          placeholder="/sayfa-url"
                          className="px-2 py-1 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
                        />
                      </div>
                      <button
                        onClick={() => handleRemoveLink(index, linkIndex)}
                        className="p-1 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                  
                  {(column.links || []).length === 0 && (
                    <div className="text-center p-3 bg-slate-50 rounded-lg border border-dashed border-slate-200">
                      <p className="text-xs text-slate-500 mb-1">Henüz link eklenmedi</p>
                      <button
                        onClick={() => handleAddLink(index)}
                        className="text-xs text-[#F7BA45] hover:underline"
                      >
                        Link Ekle
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
          
          {(content?.columns || []).length === 0 && (
            <div className="text-center p-6 bg-slate-50 rounded-lg border border-dashed border-slate-200">
              <p className="text-slate-500 mb-2">Henüz sütun eklenmedi</p>
              <button
                onClick={handleAddColumn}
                className="inline-flex items-center gap-1 px-3 py-1.5 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors text-sm"
              >
                <Plus size={16} />
                <span>İlk Sütunu Ekle</span>
              </button>
            </div>
          )}
        </div>
      </div>
      
      {/* Copyright */}
      <div className="space-y-4">
        <h3 className="text-sm font-medium text-slate-700">Telif Hakkı</h3>
        
        <div className="space-y-2">
          <label className="block text-sm text-slate-600">
            Telif Hakkı Metni
          </label>
          <input
            type="text"
            name="copyrightText"
            value={content?.copyrightText || `© ${new Date().getFullYear()} KrediPark. Tüm hakları saklıdır.`}
            onChange={(e) => onContentChange({ copyrightText: e.target.value })}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
          />
        </div>
        
        <div className="space-y-2">
          <label className="block text-sm text-slate-600">
            Şirket Bilgisi
          </label>
          <input
            type="text"
            name="companyInfo"
            value={content?.companyInfo || 'Technoworks Bilgi Teknolojileri A.Ş.'}
            onChange={(e) => onContentChange({ companyInfo: e.target.value })}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
          />
        </div>
        
        <div className="space-y-2">
          <label className="block text-sm text-slate-600">
            Mersis No
          </label>
          <input
            type="text"
            name="mersisNo"
            value={content?.mersisNo || '833118794400001'}
            onChange={(e) => onContentChange({ mersisNo: e.target.value })}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
          />
        </div>
      </div>
    </div>
  );
};

export default FooterEditor;